import { useState, useEffect } from 'react'
import { 
  Card, Button, Form, Input, Select, Steps, message,
  Typography, Space, Tabs, Tag, Tree
} from 'antd'
import { 
  DatabaseOutlined, CloudServerOutlined, 
  CheckCircleOutlined, LoadingOutlined, TableOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '../../lib/api'
import { useAppSelector } from '../../store/hooks'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const { Option } = Select

// Changed: Use NestJS backend with /v1 prefix
const BACKEND_API_URL = 'http://localhost:8000/api/v1'
const FINETUNING_API_URL = 'http://localhost:5000' // Python API

const SchemaGeneratorPage = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)

  // Get current project from Redux store (already selected in ThiranAI app)
  const { currentProject } = useAppSelector((state) => state.project)

  const [dataSources, setDataSources] = useState([])
  const [preview, setPreview] = useState<any>(null)
  const [jsonlSamples, setJsonlSamples] = useState([])
  
  // New state for schema selection
  const [schemaStructure, setSchemaStructure] = useState<any>(null)
  const [checkedKeys, setCheckedKeys] = useState<any[]>([])
  const [treeData, setTreeData] = useState<any[]>([])
  const [tableSearchQuery, setTableSearchQuery] = useState<string>('')
  const [formValues, setFormValues] = useState<any>({}) // Store form values across steps
  
  // New: Job tracking for async processing
  const [_jobId, setJobId] = useState<string | null>(null)
  const [_jobStatus, setJobStatus] = useState<any>(null)

  // Load data sources for current project on mount
  useEffect(() => {
    if (currentProject) {
      loadDataSources(currentProject.id)
    }
  }, [currentProject])

  const loadDataSources = async (projectId: string) => {
    try {
      // Call ThiranAI backend directly - use correct endpoint
      const response = await api.get(`/projects/${projectId}/data-sources`)
      setDataSources(response.data || [])
    } catch (error) {
      message.error('Failed to load data sources')
      setDataSources([])
    }
  }

  // Step 1 -> Step 2: Fetch schema structure for selection (NOW CALLS NESTJS!)
  const handleFetchSchema = async (values: any) => {
    setLoading(true)
    setFormValues(values) // Save form values for later use
    try {
      const token = localStorage.getItem('token')
      
      // Call NestJS backend directly for schema structure
      const response = await axios.get(
        `${BACKEND_API_URL}/training-datasets/datasource/${values.dataSourceId}/schema-structure?projectId=${currentProject!.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      )
      
      const structure = response.data
      setSchemaStructure(structure)
      
      // Build Tree Data from schemaTree (optimized - no columns)
      const tree = [{
        title: structure.databaseName,
        key: 'root',
        icon: <DatabaseOutlined />,
        children: structure.schemaTree.map((table: any) => ({
          title: (
            <Space>
              <span>{table.title}</span>
              <Tag style={{ fontSize: '10px' }}>{table.columnCount || 0} cols</Tag>
              {table.rowCount != null && <Tag color="blue" style={{ fontSize: '10px' }}>{table.rowCount?.toLocaleString()} rows</Tag>}
            </Space>
          ),
          key: table.title, // Use title (table name) as key
          icon: <TableOutlined />,
          isLeaf: true
        }))
      }]
      
      setTreeData(tree)
      // Default: Check all tables
      setCheckedKeys(structure.schemaTree.map((t: any) => t.title))
      
      setStep(1)
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to fetch schema structure')
    } finally {
      setLoading(false)
    }
  }

  // Step 2 -> Step 3: Extract Schema (Start Async Job)
  const handleExtract = async () => {
    if (checkedKeys.length === 0) {
      message.warning('Please select at least one table')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      
      // Filter out 'root' key if present (it's not a table)
      const selectedTables = checkedKeys.filter(key => key !== 'root')
      
      // Call NestJS to start async job
      const response = await axios.post(
        `${BACKEND_API_URL}/training-datasets/generate-from-datasource`, 
        {
          projectId: currentProject!.id,
          dataSourceId: formValues.dataSourceId,
          name: formValues.name,
          description: formValues.description,
          selectedTables: selectedTables
        }, 
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      )

      const { jobId } = response.data
      setJobId(jobId)
      message.loading({ content: 'Generating dataset...', key: 'generation' })
      
      // Start polling
      pollJobStatus(jobId)

    } catch (error: any) {
      setLoading(false)
      message.error(error.response?.data?.message || 'Failed to start generation job')
    }
  }

  const pollJobStatus = async (id: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(
        `${BACKEND_API_URL}/training-datasets/jobs/${id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      )
      
      const status = response.data
      setJobStatus(status)

      if (status.status === 'completed') {
        setLoading(false)
        message.success({ content: 'Dataset generated successfully!', key: 'generation' })
        
        // Set preview with actual statistics from job
        setPreview({
          id: status.datasetId,
          statistics: {
            tables: status.statistics?.totalTables || 0,
            columns: status.statistics?.totalColumns || 0,
            examples: status.statistics?.totalExamples || 0
          },
          yamlPreview: 'Loading YAML preview...', 
        })
        
        // Fetch YAML content
        try {
          const yamlResponse = await axios.get(
            `${FINETUNING_API_URL}/api/schema/datasets/${status.datasetId}/yaml`
          )
          setPreview((prev: any) => ({
            ...prev,
            yamlPreview: yamlResponse.data.content || yamlResponse.data
          }))
        } catch (e) {
          console.error('Failed to load YAML', e)
          setPreview((prev: any) => ({
            ...prev,
            yamlPreview: '# YAML preview unavailable'
          }))
        }
        
        // Fetch JSONL samples
        try {
           const samplesResponse = await axios.get(
            `${FINETUNING_API_URL}/api/schema/datasets/${status.datasetId}/sample?limit=5`
           )
           setJsonlSamples(samplesResponse.data.sample_examples)
        } catch (e) {
           console.error('Failed to load samples', e)
        }
        
        setStep(3) // Move to step 3 (Preview)
      } else if (status.status === 'failed') {
        setLoading(false)
        message.error({ content: `Generation failed: ${status.error}`, key: 'generation' })
      } else {
        // Still processing, poll again in 2s
        setTimeout(() => pollJobStatus(id), 2000)
      }
    } catch (error) {
      setLoading(false)
      message.error({ content: 'Failed to poll job status', key: 'generation' })
    }
  }

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await axios.post(`${FINETUNING_API_URL}/api/schema/datasets/${preview.id}/confirm`)
      message.success('Dataset confirmed and ready for training!')
      setStep(3)
    } catch (error) {
      message.error('Failed to confirm dataset')
    } finally {
      setLoading(false)
    }
  }

  const onCheck = (checkedKeysValue: any) => {
    setCheckedKeys(checkedKeysValue)
  }

  // Show message if no project selected
  if (!currentProject) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Card>
          <Title level={3}>No Project Selected</Title>
          <Text type="secondary">
            Please select a project from the header before generating a dataset.
          </Text>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Title level={2}>
          <DatabaseOutlined /> Generate from Database Schema
        </Title>
        <Text type="secondary">
          Automatically extract database schema and generate training data for <strong>{currentProject.name}</strong>
        </Text>
      </div>

      <Steps current={step} style={{ marginBottom: '32px' }}>
        <Steps.Step title="Select Source" icon={step === 0 && loading ? <LoadingOutlined /> : undefined} />
        <Steps.Step title="Select Tables" icon={step === 1 && loading ? <LoadingOutlined /> : undefined} />
        <Steps.Step title="Preview" icon={step === 2 && loading ? <LoadingOutlined /> : undefined} />
        <Steps.Step title="Confirm" />
      </Steps>

      {/* Step 1: Select Data Source */}
      {step === 0 && (
        <Card>
          <Form form={form} layout="vertical" onFinish={handleFetchSchema}>
            {/* Show current project info */}
            <div style={{ 
              padding: '12px', 
              background: '#f0f7ff', 
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid #91d5ff'
            }}>
              <Text type="secondary">Current Project:</Text>
              <br />
              <Text strong style={{ fontSize: '16px' }}>{currentProject.name}</Text>
            </div>

            <Form.Item
              label="Data Source"
              name="dataSourceId"
              rules={[{ required: true, message: 'Please select a data source' }]}
            >
              <Select
                placeholder="Select a data source"
                showSearch
                optionFilterProp="children"
                loading={dataSources.length === 0}
              >
                {dataSources.map((source: any) => (
                  <Option key={source.id} value={source.id}>
                    <Space>
                      <CloudServerOutlined />
                      {source.name}
                      <Tag>{source.connectorType}</Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Dataset Name"
              name="name"
              rules={[{ required: true, message: 'Please enter dataset name' }]}
            >
              <Input placeholder="e.g., Production Database Schema" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <TextArea rows={3} placeholder="Describe this dataset..." />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<DatabaseOutlined />}
                >
                  Load Schema Structure
                </Button>
                <Button onClick={() => navigate('/training/datasets/list')}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* Step 2: Select Tables (Tree View) */}
      {step === 1 && schemaStructure && (() => {
        // Filter tree based on search query
        const filteredTree = tableSearchQuery.trim() 
          ? [{
              ...treeData[0],
              children: treeData[0].children?.filter((table: any) => 
                table.key.toLowerCase().includes(tableSearchQuery.toLowerCase())
              ) || []
            }]
          : treeData
        
        const filteredCount = filteredTree[0]?.children?.length || 0
        
        return (
          <Card title="Select Tables to Include">
            {/* Summary and Search */}
            <div style={{ marginBottom: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Text type="secondary">
                  Select the tables you want to include in the training dataset. We found <strong>{schemaStructure.schemaTree.length}</strong> tables in <strong>{schemaStructure.databaseName}</strong>.
                </Text>
                
                <Input.Search
                  placeholder="Search tables by name..."
                  allowClear
                  size="large"
                  value={tableSearchQuery}
                  onChange={(e) => setTableSearchQuery(e.target.value)}
                  style={{ maxWidth: '500px' }}
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                />
                
                {/* Table Selection Counter */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '12px 16px',
                  background: '#e6f7ff',
                  borderRadius: '6px',
                  border: '1px solid #91d5ff'
                }}>
                  <TableOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                  <div>
                    <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                      {checkedKeys.filter(k => k !== 'root').length} / {schemaStructure.schemaTree.length} tables selected
                    </Text>
                    {checkedKeys.filter(k => k !== 'root').length === 0 && (
                      <div><Text type="warning" style={{ fontSize: '12px' }}>Please select at least one table</Text></div>
                    )}
                  </div>
                </div>
                
                {tableSearchQuery && (
                  <Text type="secondary">
                    Showing {filteredCount} of {schemaStructure.schemaTree.length} tables
                  </Text>
                )}
              </Space>
            </div>
            
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: '8px', 
              padding: '16px',
              maxHeight: '400px',
              overflow: 'auto',
              background: '#fafafa'
            }}>
              <Tree
                checkable
                defaultExpandAll
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                treeData={filteredTree}
                showIcon
              />
          </div>

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
            <Space>
               <Button onClick={() => setCheckedKeys(schemaStructure.schemaTree.map((t: any) => t.title))}>
                 Select All
               </Button>
               <Button onClick={() => setCheckedKeys([])}>
                 Deselect All
               </Button>
            </Space>
            <Space>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button 
                type="primary" 
                onClick={handleExtract} 
                loading={loading}
                icon={<DatabaseOutlined />}
                disabled={checkedKeys.length === 0}
              >
                Generate Dataset ({checkedKeys.filter(k => k !== 'root').length} tables)
              </Button>
            </Space>
          </div>
        </Card>
        )
      })()}

      {/* Step 3: Preview */}
      {step === 2 && preview && (
        <Card>
          {/* Statistics */}
          <div style={{ 
            display: 'flex', 
            gap: '24px', 
            padding: '20px', 
            background: '#f5f5f5', 
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary">Tables</Text>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>
                {preview.statistics.tables}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary">Columns</Text>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>
                {preview.statistics.columns}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary">Training Examples</Text>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>
                {preview?.statistics?.examples?.toLocaleString() || '0'}
              </div>
            </div>
          </div>

          {/* Preview Tabs */}
          <Tabs defaultActiveKey="yaml">
            <Tabs.TabPane tab="YAML Schema" key="yaml">
              <pre style={{ 
                background: '#f9f9f9', 
                padding: '16px', 
                borderRadius: '4px',
                maxHeight: '400px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {preview.yamlPreview}
              </pre>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Training Data (JSONL)" key="jsonl">
              <div>
                {jsonlSamples.slice(0, 5).map((sample: any, idx: number) => (
                  <Card key={idx} size="small" style={{ marginBottom: '12px' }}>
                    <div><strong>Q:</strong> {sample.user_message}</div>
                    <div style={{ marginTop: '8px' }}>
                      <strong>A:</strong> <code>{sample.assistant_response}</code>
                    </div>
                  </Card>
                ))}
                <Text type="secondary">
                  Showing 5 of {preview.statistics.examples} examples
                </Text>
              </div>
            </Tabs.TabPane>
          </Tabs>

          {/* Actions */}
          <div style={{ marginTop: '24px' }}>
            <Space>
              <Button onClick={() => setStep(1)}>Back to Selection</Button>
              <Button 
                type="primary" 
                onClick={handleConfirm} 
                loading={loading}
                icon={<CheckCircleOutlined />}
              >
                Confirm & Create Dataset
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Step 4: Success */}
      {step === 3 && preview && (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircleOutlined 
            style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }} 
          />
          <Title level={3}>Dataset Created Successfully!</Title>
          <Paragraph>
            Your training dataset is ready with {preview.statistics.examples.toLocaleString()} examples.
          </Paragraph>

          <Space style={{ marginTop: '24px' }}>
            <Button 
              type="primary" 
              onClick={() => navigate(`/training/datasets/${preview.id}/search`)}
            >
              View & Search Dataset
            </Button>
            <Button onClick={() => navigate('/training/datasets/list')}>
              Go to Datasets
            </Button>
          </Space>
        </Card>
      )}
    </div>
  )
}

export default SchemaGeneratorPage
